<template>
  <!-- One-time absence courtesy (backend billing/absence_courtesy.py) — admins only.
       `info` is one attendee from GET /absence-courtesies/lesson/{id}. -->
  <span v-if="info?.courtesy" class="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300"
        :title="courtesyTitle">
    Courtesy −{{ dollars(info.courtesy.amount_cents) }}<template v-if="!compact"> · {{ courtesySource }}</template>
  </span>
  <span v-else-if="info?.enabled && info.unavailable === 'package'" class="text-xs text-fg-faint dark:text-fg-faint-dark italic">
    Courtesy n/a — lesson package
  </span>
  <span v-else-if="info?.grantable_cents > 0" class="inline-flex items-center gap-1 text-xs">
    <template v-if="!confirming">
      <button @click="confirming = true" class="px-1.5 py-0.5 rounded border border-stroke dark:border-stroke-dark text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark">
        Grant courtesy (−{{ dollars(info.grantable_cents) }})
      </button>
    </template>
    <template v-else>
      <span class="text-fg-muted dark:text-fg-muted-dark">Emails the family.</span>
      <button :disabled="saving" @click="grant" class="px-1.5 py-0.5 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
        {{ saving ? 'Granting…' : 'Grant' }}
      </button>
      <button :disabled="saving" @click="confirming = false" class="px-1.5 py-0.5 rounded text-fg-muted dark:text-fg-muted-dark">Cancel</button>
    </template>
    <span v-if="error" class="text-red-500 dark:text-red-400">{{ error }}</span>
  </span>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useApi } from '@/composables/useApi.js'

const props = defineProps({
  lessonId: { type: Number, required: true },
  info:     { type: Object, default: null },
  compact:  { type: Boolean, default: false },
})
const emit = defineEmits(['updated'])
const { fetchApi } = useApi()

const confirming = ref(false)
const saving     = ref(false)
const error      = ref(null)

const dollars = cents => `$${(cents / 100).toFixed(2)}`
const courtesySource = computed(() => {
  const c = props.info.courtesy
  return c.source === 'auto' ? 'automatic' : `granted by ${c.granted_by || 'staff'}`
})
const courtesyTitle = computed(() => {
  const c = props.info.courtesy
  const when = new Date(c.granted_at).toLocaleDateString('en-US', { timeZone: 'America/New_York' })
  return `One-time absence courtesy, ${courtesySource.value} on ${when}`
})

async function grant() {
  saving.value = true
  error.value = null
  try {
    const rows = await fetchApi(`/api/absence-courtesies/lesson/${props.lessonId}/grant`, {
      method: 'POST', body: JSON.stringify({ student_id: props.info.student_id }),
    })
    confirming.value = false
    emit('updated', rows)
  } catch (e) {
    error.value = e.detail || e.message
  } finally {
    saving.value = false
  }
}
</script>
